import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Snail — a dozy garden snail, storybook style: a soft cream-olive foot
// stretching forward into a raised head, a big amber swirl shell riding on its
// back (a torus plus stacked spheres for the spiral read) and two tall eye
// stalks with big ball eyes. Faces +Z, the foot lies flat on y = 0. About
// 0.35 m long, shell top near 0.29 m.
//
// Rig layout (flat — each named node moves about its own pivot):
//   - "body"    : the foot + head + face. Stretches gently in idle.
//   - "shell"   : the whole swirl shell. Rocks along in idle.
//   - "stalk-l" / "stalk-r" : the eye stalks with their ball eyes. The
//     wiggle's star parts, waving about their roots on the head.

const SKIN = "#cfc39a";       // soft olive-cream foot
const SKIN_DARK = "#b0a67e";  // shaded underside of the foot
const CREAM = "#f6efe3";      // belly ribbon + highlights
const SHELL = "#cf8a4e";      // warm amber shell
const SHELL_DARK = "#a5683a"; // the darker spiral band
const EYE_WHITE = "#f7f3e8";  // eye whites (never pure white)
const PUPIL = "#332b33";      // dark plum pupils

// The pivots the animations move the named nodes about.
const BODY_PIVOT: [number, number, number] = [0, 0, -0.14];        // the tail end
const SHELL_PIVOT: [number, number, number] = [0, 0.05, -0.05];    // where it rides the foot
const STALK_L_PIVOT: [number, number, number] = [-0.028, 0.145, 0.135];
const STALK_R_PIVOT: [number, number, number] = [0.028, 0.145, 0.135];

// One eye stalk: a soft tapered stalk leaning up-forward from the head, with a
// big ball eye on top (white ball, dark pupil forward, tiny highlight).
function stalk(side: 1 | -1): Part[] {
  const name = side > 0 ? "stalk-r" : "stalk-l";
  const tipX = side * 0.048, tipY = 0.235, tipZ = 0.16;
  return [
    { geometry: new THREE.CylinderGeometry(0.010, 0.014, 0.10, 8), color: SKIN,
      position: [side * 0.038, 0.19, 0.148], rotation: [0.18, 0, -side * 0.22], name },
    { geometry: new THREE.SphereGeometry(0.030, 10, 8), color: EYE_WHITE,
      position: [tipX, tipY, tipZ], name },
    { geometry: new THREE.SphereGeometry(0.016, 8, 6), color: PUPIL,
      position: [tipX, tipY, tipZ + 0.021], name },
    { geometry: new THREE.SphereGeometry(0.006, 6, 5), color: CREAM,
      position: [tipX + side * 0.007, tipY + 0.009, tipZ + 0.030], name },
  ];
}

export const snail = defineModel({
  id: "snail",
  batchId: "mess",
  name: "Snail",
  tags: ["mess", "cute", "animated", "creature", "snail", "mollusc", "bug", "garden", "slow"],
  summary: "A dozy cream-olive snail with a big amber swirl shell and ball-eyed stalks.",
  description:
    "A storybook garden snail: a soft cream-olive foot stretching forward into " +
    "a raised head, a big amber swirl shell riding its back and two tall eye " +
    "stalks topped with big ball eyes. Stretches and settles at rest (idle) " +
    "and waves its eye stalks about (wiggle).",
  author: "models",
  footprint: 0.2,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      // ── foot + head (named, stretches) ───────────────────────────────────
      // The long soft foot, flat on the ground.
      { geometry: new THREE.SphereGeometry(0.09, 12, 10), color: SKIN,
        position: [0, 0.055, 0.0], scale: [0.75, 0.62, 1.65], name: "body" },
      // A darker skirt along the very bottom of the foot.
      { geometry: new THREE.SphereGeometry(0.088, 12, 10), color: SKIN_DARK,
        position: [0, 0.035, 0.0], scale: [0.78, 0.38, 1.68], name: "body" },
      // The head rising at the front.
      { geometry: new THREE.SphereGeometry(0.062, 12, 10), color: SKIN,
        position: [0, 0.115, 0.135], scale: [1, 1.15, 1], name: "body" },
      // A cream chin/chest patch under the face.
      { geometry: new THREE.SphereGeometry(0.040, 10, 8), color: CREAM,
        position: [0, 0.085, 0.165], scale: [1, 0.9, 0.7], name: "body" },
      // A tiny happy mouth: a soft dark dimple low on the face.
      { geometry: new THREE.SphereGeometry(0.012, 8, 6), color: SKIN_DARK,
        position: [0, 0.105, 0.192], scale: [1.4, 0.7, 0.5], name: "body" },

      // ── shell (named, rocks) ─────────────────────────────────────────────
      // The main swirl: a fat torus standing upright (plane facing +/-X),
      // sunk into the back of the foot so it reads mounted, not perched.
      { geometry: new THREE.TorusGeometry(0.066, 0.052, 10, 20), color: SHELL,
        position: [0, 0.150, -0.06], rotation: [0, Math.PI / 2, 0], name: "shell" },
      // The spiral centre: a darker heart filling the torus hole...
      { geometry: new THREE.SphereGeometry(0.050, 10, 8), color: SHELL_DARK,
        position: [0, 0.150, -0.06], scale: [0.85, 1, 1], name: "shell" },
      // ...and a small cream tip marking the very middle of the swirl.
      { geometry: new THREE.SphereGeometry(0.024, 8, 6), color: CREAM,
        position: [0.026, 0.150, -0.06], scale: [0.6, 1, 1], name: "shell" },
      // The whorl trailing off, tucked between the shell rim and the tail.
      { geometry: new THREE.SphereGeometry(0.042, 10, 8), color: SHELL_DARK,
        position: [0, 0.082, -0.125], scale: [0.85, 0.75, 1.0], name: "shell" },

      // ── eye stalks (named, wiggle) ───────────────────────────────────────
      ...stalk(1), ...stalk(-1),
    ];
  },
  animations: [
    // Idle — a slow, dozy stretch: the foot lengthens a touch (about the tail,
    // so the nose leads), the shell rocks along and the stalks drift.
    {
      id: "idle",
      label: "Idle stretch",
      duration: 3.6,
      loop: true,
      tracks: [
        {
          target: "body", pivot: BODY_PIVOT,
          scale: [
            { t: 0, value: [1, 1, 1], ease: "sine" },
            { t: 1.8, value: [0.99, 1.02, 1.045], ease: "sine" },
            { t: 3.6, value: [1, 1, 1], ease: "sine" },
          ],
        },
        {
          target: "shell", pivot: SHELL_PIVOT,
          rotation: [
            { t: 0, value: [0.03, 0, 0], ease: "sine" },
            { t: 1.8, value: [-0.03, 0, 0], ease: "sine" },
            { t: 3.6, value: [0.03, 0, 0], ease: "sine" },
          ],
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.8, value: [0, 0.006, 0.01], ease: "sine" },
            { t: 3.6, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "stalk-l", pivot: STALK_L_PIVOT,
          rotation: [
            { t: 0, value: [0.04, 0, 0.03], ease: "sine" },
            { t: 1.8, value: [-0.04, 0, -0.03], ease: "sine" },
            { t: 3.6, value: [0.04, 0, 0.03], ease: "sine" },
          ],
        },
        {
          target: "stalk-r", pivot: STALK_R_PIVOT,
          rotation: [
            { t: 0, value: [0.04, 0, -0.03], ease: "sine" },
            { t: 1.8, value: [-0.04, 0, 0.03], ease: "sine" },
            { t: 3.6, value: [0.04, 0, -0.03], ease: "sine" },
          ],
        },
      ],
    },

    // Wiggle — the signature move: the eye stalks wave about their roots in
    // counter-phase, like a puzzled periscope pair, with a curious head tilt.
    {
      id: "wiggle",
      label: "Eye-stalk wiggle",
      duration: 2.0,
      loop: true,
      tracks: [
        {
          target: "stalk-l", pivot: STALK_L_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.5, value: [-0.25, 0, 0.35], ease: "sine" },
            { t: 1.0, value: [0.15, 0, 0], ease: "sine" },
            { t: 1.5, value: [-0.25, 0, -0.2], ease: "sine" },
            { t: 2.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "stalk-r", pivot: STALK_R_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.5, value: [0.15, 0, 0.2], ease: "sine" },
            { t: 1.0, value: [-0.25, 0, 0], ease: "sine" },
            { t: 1.5, value: [0.15, 0, -0.35], ease: "sine" },
            { t: 2.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "body", pivot: BODY_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.0, value: [0, 0.04, 0], ease: "sine" },
            { t: 2.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
